import React from 'react'
import CtaButton from 'components/cta-button'
import Font from '@cred/react-font'
import { Testable } from '@cred/react-testable'
import { onlyMobile, exceptMobile } from 'common/visibility'
import { product as spProduct, component as spContext } from '@cred/react-analytics/contexts'
import PropTypes from 'prop-types'
import { currency, number } from '@cred/shared/formatters'
import { withStyles } from '@cred/react-theme'

import lenderDashboardImages from '../lender-logos'
import defaultStyles from './styles.module.scss'
import homes1 from './homes1.module.scss'
import multiVerticalStyles from './multiVerticalStyles.scss'

const getFontTypesFromMultivertical = isMultiVertical => ({
  medium: isMultiVertical ? 'disclaimer' : 'body',
  large: isMultiVertical ? 'bodyLarge' : 'large',
})

const ProductTile = ({
  product: {
    uuid,
    externalProductId,
    lender: { name, slug, nmls },
    monthlyPayment,
    apr,
    rate,
    fees,
    loanTerm,
    amortizationType,
  },
  onGetStarted,
  dashboardContexts,
  isMultiVertical,
  styles: defaultStylesProps,
}) => {
  const styles = isMultiVertical ? multiVerticalStyles : defaultStylesProps
  const fontTypes = getFontTypesFromMultivertical(isMultiVertical)

  return (
    <Testable className={styles.container} key={uuid} testId="product" id={externalProductId}>
      <div className={styles.firstRow}>
        <div className={styles.lenderSection}>
          <img
            className={styles.itemLenderLogo}
            src={lenderDashboardImages(slug)}
            alt={name}
          />
          <Font type="disclaimer" color="lighter" className={`mt-8 ${styles.nmlsText}`}>NMLS #{nmls}</Font>
        </div>
        <div className={`${styles.buttonSectionMobile}`}>
          <CtaButton
            className={styles.ctaMobile}
            onClick={onGetStarted}
            analyticsContexts={[...dashboardContexts, spProduct.analytics({ loanProductId: `${externalProductId}`, eligibilityProductId: '0' })]}
          >
        Get Started
          </CtaButton>
          <Font type="disclaimer" color="lighter" className={`mt-8 ${styles.ctaModifierMobile}`}>on Credible’s website</Font>
        </div>
      </div>
      <Testable className={styles.secondRow} testId="product-details">
        <Testable className={styles.itemSection} testId="monthly-payment-details">
          <Font type={fontTypes.medium} color="lighter" className={onlyMobile}>Mo. payment</Font>
          <Font type={fontTypes.medium} color="lighter" className={exceptMobile}>Monthly payment</Font>
          <Testable testId="amount">
            <Font type={fontTypes.large} weight="semiBold" className={styles.arapeyFontFamily}>{currency(monthlyPayment)}</Font>
          </Testable>
          <Testable testId="loan-type">
            {amortizationType === 'Fixed'
              && <Font type="disclaimer" weight={isMultiVertical ? 'semiBold' : ''} color={isMultiVertical ? 'secondary' : 'accent'}>{loanTerm}-year fixed</Font>}
            {amortizationType === 'ARM'
              && <Font type="disclaimer" weight={isMultiVertical ? 'semiBold' : ''} color={isMultiVertical ? 'secondary' : 'accent'}>5/1 adjustable</Font>}
          </Testable>
        </Testable>
        <Testable className={styles.itemSection} testId="apr-details">
          <Font type={fontTypes.medium} color="lighter">APR</Font>
          <Testable testId="amount">
            <Font type={fontTypes.large} weight="semiBold" className={styles.arapeyFontFamily}>{number(apr || 0, 3)}%</Font>
          </Testable>
          <Testable testId="rate-type">
            <Font type="disclaimer" weight={isMultiVertical ? 'semiBold' : ''} color={isMultiVertical ? 'secondary' : 'accent'}>{number(rate || 0, 3)}% Rate</Font>
          </Testable>
        </Testable>
        <Testable className={styles.itemSection} testId="fees-details">
          <Font type={fontTypes.medium} color="lighter">Fees</Font>
          <Testable testId="amount">
            <Font type={fontTypes.large} weight="semiBold" className={styles.arapeyFontFamily}>{currency((fees < 0 ? 0 : fees) || 0)}</Font>
          </Testable>
        </Testable>
        <div className={styles.buttonSectionDesktop}>
          <CtaButton
            className={styles.ctaDesktop}
            small={isMultiVertical}
            onClick={onGetStarted}
            analyticsContexts={
            [
              ...dashboardContexts,
              spProduct.analytics({ loanProductId: `${externalProductId}`, eligibilityProductId: '0' }),
              spContext.analytics({ name: 'Product tile', description: `${name}, ${rate}` }),
            ]
          }
          >
        Get Started
          </CtaButton>
          <Font type="disclaimer" color="lighter" className={`mt-8 ${styles.ctaModifierTablet}`}>on Credible’s website</Font>
        </div>
      </Testable>
    </Testable>
  )
}

ProductTile.propTypes = {
  product: PropTypes.object.isRequired,
  onGetStarted: PropTypes.func.isRequired,
  dashboardContexts: PropTypes.array.isRequired,
  isMultiVertical: PropTypes.bool,
  styles: PropTypes.object,
}

export default withStyles(defaultStyles, { homes1 })(ProductTile)
